/**
 * Resume Scorecard Modal
 *
 * Deterministic readout of how the resume reads — headlined by the
 * "Accuracy" score (typos/grammar — the 45% dimension). Runs on the in-memory
 * resume (including unsaved edits). Everything is rule-based and free: the
 * Authenticity/Quantification/Completeness dimensions are computed in the
 * browser, and Accuracy comes from the server's deterministic lint pass
 * (`mode: 'lint'` on the review endpoint — no LLM, no usage meter).
 *
 * Available to all users (trial/normal/active) — unlike the AI Review feature,
 * this is read-only guidance, not an AI-assisted rewrite.
 */
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { X, Gauge, ChevronDown, ChevronRight, Check, Trash2, Undo2, BookPlus, Wand2, Loader2 } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { api } from '../services/api';
import type { ResumeJSON } from '../types';
import {
  buildScorecard, scoreColor, getTextAtPath, getTextAtLintPath, humanizePath, humanizeMessage,
  findingLintPaths, lintPathToFindingPath, WEIGHTS, DEFAULT_RULE_OF_THREE_TOLERANCE,
  DEFAULT_QUANTIFICATION_TARGET, setTextAtPath, recheckFinding, findingDimension,
  findingSectionChip, deleteSkillAt, insertSkillAt, resolveLintPath, logAfterUndo,
  findingRenderTier, findingStructuralScope, findingReferenceQuote,
  rowOpenAfterResolveToggle, findingSpellingWord, applyDictionary, addToDictionary,
  collectSafeFixes, resolvedSafeFixKeys, applySafeFixes,
  deleteBulletAt, insertBulletAt, getBulletAt, isBulletDeletion, entryDeleteKind,
  bulletDeletionLabel,
  type DimensionScore, type Finding, type FindingPath, type LintFinding, type Severity,
  type RecheckResult, type SkillDeletion, type ScorecardDeletion, type BulletLocation,
  type SafeFix,
} from '../utils/scorecard';
import { overallScoreLabel } from '../utils/resumeLint';
import { BulletTextWithLineBreaks } from './BulletTextWithLineBreaks';

interface ScorecardModalProps {
  isOpen: boolean;
  onClose: () => void;
  resume: ResumeJSON;
  /** Name of the resume record being edited — only used to address the lint
   *  endpoint (`POST /resumes/{resumename}/review`). The live `resume` above is
   *  what actually gets linted, so a missing name still works: we fall back to
   *  the candidate name. Pass it when available so server logs are accurate. */
  resumename?: string;
  /** Persist an edited item back to the resume (update state + save). */
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
  /** Persist the set of manually-resolved finding ids onto the resume so a
   *  dismissal survives across sessions (saved with the resume). */
  onResolvedChange?: (resolvedIds: string[]) => void;
  /** Hand the server lint findings up to the editor so the rail bar and the
   *  top-bar chip score the SAME four dimensions this modal does (without the
   *  editor firing its own lint request). */
  onLintFindings?: (findings: LintFinding[] | null) => void;
  /** Findings the editor's shared pass already has, used as this modal's
   *  STARTING state so opening the Scorecard on an already-scored resume shows
   *  the real number immediately (its own pass then refreshes it in place). Only
   *  when the editor has nothing yet does the overall render as "calculating". */
  initialLintFindings?: LintFinding[] | null;
  /**
   * Apply an IN-MEMORY change to the resume the editor holds. Used by the merge
   * flow's Delete: unlike the per-item Save (which persists immediately) this
   * never calls the API — the removal rides along with the user's next resume
   * Save, so Cancel still discards it, exactly like any other editor edit.
   */
  onEditorMutate?: (mutate: (resume: ResumeJSON) => ResumeJSON) => void;
  /**
   * "Fix all N safe issues" (BR-2). Applies ONE batched in-memory mutation and
   * persists it with ONE save — never a save per item, which would re-trigger
   * the resume tag lifecycle N times. Rejects if the save fails, so the panel
   * can say so and nothing is claimed. Absent → the control isn't offered.
   */
  onBatchFix?: (mutate: (resume: ResumeJSON) => ResumeJSON) => Promise<void>;
  /**
   * Opened from the "issues in this master resume" warning (BR-1): lead with
   * the high-severity findings. The user can switch back to everything.
   */
  focusHigh?: boolean;
}

// Tooltip shown on every "Rule-of-three list" finding so users understand the
// (narrow) logic and that they can permanently dismiss intentional ones.
const RULE_OF_THREE_HELP =
  'Flagged as a rule-of-three: three single-word items in a list ' +
  '(e.g. "fast, reliable, and scalable") — a cadence that reads as AI-written. ' +
  'Lists of four or more items, and multi-word items, are NOT flagged. ' +
  'If this phrasing is intentional, click "Mark resolved" below to dismiss it — ' +
  'it stays dismissed once you save your resume.';

const SEV_DOT: Record<Severity, string> = { high: 'bg-red-500', med: 'bg-amber-500', low: 'bg-gray-400' };

/**
 * Section chip that LEADS every finding row (owner request 2026-07-27): the
 * trailing gray breadcrumb was easy to miss, so a duplicate-SKILL finding read
 * as an experience-bullet problem. One neutral chip color for every section —
 * the rail's band colors are POSITIONAL (a user can drag Skills into any band),
 * so they can't name a section reliably, and ten tinted chips per screen would
 * compete with the severity dots that actually carry meaning.
 */
function SectionChip({ section }: { section: string }) {
  const name = findingSectionChip(section);
  return (
    <span
      data-testid="scorecard-finding-section"
      data-section={name}
      className="inline-flex items-center mr-1.5 px-1.5 py-px rounded-full border border-gray-300 bg-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-600 align-[1px] whitespace-nowrap"
      title={`This item is in your ${name} section`}
    >
      {name}
    </span>
  );
}

/**
 * Overall readiness ring. While the Accuracy (server lint) pass is still in
 * flight the overall is NOT knowable — Accuracy is 45% of it — so the ring
 * shows the neutral placeholder and an empty arc instead of the lint-less
 * (higher) number that is about to drop. Same rule as the editor rail + chip.
 */
function ScoreRing({ score, pending }: { score: number; pending?: boolean }) {
  const c = scoreColor(score);
  const r = 34, circ = 2 * Math.PI * r;
  const dash = pending ? 0 : (Math.max(0, Math.min(100, score)) / 100) * circ;
  const stroke = score >= 85 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div
      className="relative w-24 h-24 flex-shrink-0"
      data-testid="scorecard-overall"
      data-score={pending ? '' : String(score)}
      data-computing={pending ? '1' : '0'}
      title={pending ? 'Calculating your resume score (checking spelling and grammar)…' : undefined}
    >
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        {!pending && (
          <circle cx="40" cy="40" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
        )}
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${pending ? 'text-gray-400 animate-pulse' : c.text}`}>
        <span className="text-2xl font-bold leading-none">{overallScoreLabel(score, pending)}</span>
        <span className="text-[10px] text-gray-400">/ 100</span>
      </div>
    </div>
  );
}

interface FindingEntryProps {
  /** Lint path grammar for ONE referenced item (`experience[0].roles[1].bullets[2]`). */
  lintPath: string;
  resume: ResumeJSON;
  /**
   * Deletions (skills AND experience bullets) applied in THIS modal session.
   * `lintPath` was captured when the modal opened, so it is re-addressed through
   * this log before anything is read or written — otherwise a pending edit would
   * land on the item that shifted into the deleted one's index.
   */
  deletions: ScorecardDeletion[];
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
  /** Remove `skills[index]` from the editor's in-memory resume; returns the result. */
  onDeleteSkill?: (index: number) => ResumeJSON;
  /** Remove one experience bullet from the editor's in-memory resume. */
  onDeleteBullet?: (at: BulletLocation) => ResumeJSON;
  /** Put back the item removed by log entry `logIndex`; returns the result. */
  onUndoDelete?: (logIndex: number) => ResumeJSON;
  /** Fired after a save / delete / undo so the owning row can recheck itself. */
  onChanged?: (updatedResume: ResumeJSON) => void;
}

/**
 * One addressed entry inside an expanded finding. A finding can reference
 * SEVERAL items — repeated phrasing across two bullets, or the two near-identical
 * skills a duplicate-skill finding wants MERGED — and each gets its own
 * humanized label, its own current text and its own Save, so the whole set is
 * actionable from the row. Items the scorecard can't edit in place (education
 * titles, company names) render read-only.
 *
 * SKILLS get a Delete, because "merge these two" needs one of them gone.
 * EXPERIENCE BULLETS get one too (owner request 2026-08-19) — a flagged bullet
 * the user would rather drop than reword — but behind an inline CONFIRM step,
 * because a bullet is unique prose rather than one half of a duplicate pair.
 * Deleting is editor-state-only (no API call) — it rides along with the user's
 * next resume Save and is undoable here until then, matching the editor's normal
 * unsaved-changes model.
 */
function FindingEntry({
  lintPath, resume, deletions, onSaveItem, onDeleteSkill, onDeleteBullet, onUndoDelete, onChanged,
}: FindingEntryProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Bullet delete is a two-step: the button arms the confirm, the confirm does
  // it. Local to this entry, so arming one row never arms another, and it
  // disarms on its own when the entry re-renders as removed.
  const [confirming, setConfirming] = useState(false);
  const { path: livePath, deletedBy } = resolveLintPath(lintPath, deletions);
  const findingPath = livePath ? lintPathToFindingPath(livePath) : undefined;
  const editable = !!findingPath && !!onSaveItem;
  const deleteKind = entryDeleteKind(findingPath);
  const deletable = (deleteKind === 'skill' && !!onDeleteSkill)
    || (deleteKind === 'bullet' && !!onDeleteBullet);
  // Experience bullets get the PDF-aware editor (line-break ↵ markers, orphan
  // highlighting) — the same control used in Edit Resume. Other sections use
  // the plain textarea.
  const isBullet = findingPath?.kind === 'bullet';
  const liveText = findingPath
    ? getTextAtPath(resume, findingPath)
    : (livePath ? getTextAtLintPath(resume, livePath) : '');
  const value = draft ?? liveText;
  const dirty = draft !== null && draft !== liveText;
  // Label the item by where it lives NOW; a removed one keeps the text it had.
  const label = livePath ? humanizePath(livePath, resume) : humanizePath(lintPath, resume);

  async function handleItemSave() {
    if (!findingPath || !onSaveItem || !dirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveItem(findingPath, value);
      setDraft(null);
      onChanged?.(setTextAtPath(resume, findingPath, value));
    } catch {
      setSaveError('Save failed — please try again.');
    } finally {
      setSaving(false);
    }
  }

  // ── Removed block: struck through, with an in-session Undo ────────────────
  if (deletedBy !== null) {
    const removed = deletions[deletedBy];
    const removedLabel = removed && isBulletDeletion(removed)
      // A removed bullet is gone from the resume, so its own text (kept in the
      // log) is what names it — `label` would address whatever shifted up.
      ? bulletDeletionLabel(removed, resume)
      : `Skills › ${(removed as SkillDeletion | undefined)?.item?.title || label}`;
    return (
      <div
        className="rounded border border-dashed border-gray-300 bg-gray-50 p-2 space-y-1 min-w-0 box-border"
        data-testid="scorecard-entry"
        data-deleted="1"
        data-deleted-kind={removed && isBulletDeletion(removed) ? 'bullet' : 'skill'}
      >
        <div className="text-[11px] font-medium text-gray-400 line-through break-words">
          {removedLabel}
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] text-gray-500">
            Removed — save your resume to keep this change.
          </span>
          <button
            type="button"
            onClick={() => { const updated = onUndoDelete?.(deletedBy); if (updated) onChanged?.(updated); }}
            data-testid="scorecard-entry-undo"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-white"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-gray-200 bg-white p-2 space-y-1.5 min-w-0 box-border" data-testid="scorecard-entry">
      <div className="text-[11px] font-medium text-gray-500 break-words">{label}</div>
      {editable && isBullet ? (
        // PDF-accurate bullet editor. It renders at the true PDF content width
        // (743px) so each computed line stays on one visual row, and the modal
        // (max-w-[960px]) is sized to fit that comfortably. `fitContainer` caps
        // it at the card's inner width so it can NEVER overflow horizontally on
        // a narrower screen — the ↵ markers and line count come from PDF
        // metrics, so they stay accurate even if the display re-wraps.
        <div className="min-w-0">
          <BulletTextWithLineBreaks text={value} onChange={(t) => setDraft(t)} fitContainer />
        </div>
      ) : editable ? (
        <textarea
          value={value}
          onChange={(e) => setDraft(e.target.value)}
          rows={Math.min(8, Math.max(3, Math.ceil(value.length / 70)))}
          className="w-full box-border text-xs border border-gray-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
          aria-label={`Edit item: ${label}`}
        />
      ) : value ? (
        <p className="text-xs text-gray-600 break-words [overflow-wrap:anywhere]">“{value}”</p>
      ) : (
        <p className="text-xs text-gray-400">Edit this item in its resume section.</p>
      )}
      {saveError && <p className="text-xs text-red-600">{saveError}</p>}
      {(editable || deletable) && (
        <div className="flex items-center gap-2 flex-wrap">
          {editable && (
            <button
              type="button"
              // Keep focus in the editor on mousedown: the bullet control collapses
              // from a textarea back to its rendered-lines view when it loses focus,
              // and that relayout used to move this button out from under the cursor
              // between mousedown and mouseup — so the click never registered.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleItemSave}
              disabled={!dirty || saving}
              data-testid="scorecard-entry-save"
              className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {/* Skills keep their ONE-CLICK delete: it is the merge flow's second
              half ("keep one of these two"), the row already says which item it
              is, and the removed block offers an immediate Undo. */}
          {deletable && findingPath?.kind === 'skill' && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const updated = onDeleteSkill?.(findingPath.index);
                if (updated) onChanged?.(updated);
              }}
              data-testid="scorecard-entry-delete"
              data-kind="skill"
              title="Remove this skill from your resume (undoable here; applied when you save the resume)"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          {/* Bullets are unique prose, not one half of a duplicate pair, so the
              delete is ARMED first and confirmed second (owner request). Same
              inline Confirm/Cancel shape the admin Work Queue uses — never a
              native browser dialog, which can't be styled or tested. */}
          {deletable && findingPath?.kind === 'bullet' && !confirming && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setConfirming(true)}
              data-testid="scorecard-entry-delete"
              data-kind="bullet"
              title="Remove this bullet from your resume (undoable here; applied when you save the resume)"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete this bullet
            </button>
          )}
          {deletable && findingPath?.kind === 'bullet' && confirming && (
            <span
              className="inline-flex items-center gap-2 flex-wrap px-2 py-1 rounded border border-red-200 bg-red-50"
              data-testid="scorecard-entry-delete-confirm-bar"
            >
              <span className="text-[11px] text-red-800">
                Delete this bullet? This removes it from your resume.
              </span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setConfirming(false);
                  const updated = onDeleteBullet?.({
                    companyIndex: findingPath.companyIndex,
                    roleIndex: findingPath.roleIndex,
                    bulletIndex: findingPath.bulletIndex,
                  });
                  if (updated) onChanged?.(updated);
                }}
                data-testid="scorecard-entry-delete-confirm"
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                <Trash2 className="w-3.5 h-3.5" /> Confirm
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setConfirming(false)}
                data-testid="scorecard-entry-delete-cancel"
                className="px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-white"
              >
                Cancel
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface FindingRowProps {
  finding: Finding;
  resume: ResumeJSON;
  resolved: boolean;
  deletions: ScorecardDeletion[];
  onToggleResolved: () => void;
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
  onDeleteSkill?: (index: number) => ResumeJSON;
  onDeleteBullet?: (at: BulletLocation) => ResumeJSON;
  onUndoDelete?: (logIndex: number) => ResumeJSON;
  /**
   * Recheck JUST this finding against the resume as it now stands. Takes the
   * UPDATED resume (rather than a path + new text) so an EDIT, a DELETE and an
   * UNDO all recheck through one path.
   */
  onRecheck?: (finding: Finding, updatedResume: ResumeJSON) => Promise<RecheckResult>;
  /**
   * Teach the spell-checker this word (persist it to the user's preferences and
   * clear every finding for it). Rejects if the preference write fails, so the
   * row can say so and NOTHING is cleared. Absent → the button isn't offered.
   */
  onAddToDictionary?: (word: string) => Promise<void>;
}

/**
 * Outcome of the automatic recheck that runs right after an inline save.
 * SESSION-ONLY — deliberately not persisted (unlike "Mark resolved"): closing
 * and reopening the modal re-runs every check from scratch, which is the truth.
 */
type RecheckState = null | 'checking' | 'resolved' | 'flagged' | 'error';

function FindingRow({
  finding: f, resume, resolved, deletions, onToggleResolved, onSaveItem,
  onDeleteSkill, onDeleteBullet, onUndoDelete, onRecheck, onAddToDictionary,
}: FindingRowProps) {
  const [open, setOpen] = useState(false);
  const [recheck, setRecheck] = useState<RecheckState>(null);
  // "Add to My Dictionary" — offered ONLY on a spelling finding whose word we
  // can name. `null` when this is any other rule, which is what hides the button.
  const dictWord = useMemo(() => findingSpellingWord(f), [f]);
  const [dictState, setDictState] = useState<'idle' | 'adding' | 'error'>('idle');
  // Never show raw index paths: the message itself can embed them (repeated
  // phrasing lists the bullets it matched), so rewrite it too.
  const label = useMemo(() => humanizeMessage(f.label, resume), [f.label, resume]);
  // Every item this finding addresses — the server's `related` list for a
  // multi-item finding, otherwise its own path plus any referenced in the
  // message — each rendered as a labeled, individually actionable entry.
  const entries = useMemo(() => findingLintPaths(f), [f]);
  const multi = entries.length > 1;
  // What this row can actually offer: an editor, a pointer, or neither.
  const tier = useMemo(() => findingRenderTier(f), [f]);
  const editors = (tier === 'editable' || tier === 'related') && entries.length > 0;
  const scope = useMemo(() => findingStructuralScope(f, resume), [f, resume]);
  const quote = useMemo(() => findingReferenceQuote(f, resume), [f, resume]);
  const locator = entries.length === 1 ? humanizePath(entries[0], resume) : f.section;
  const complete = recheck === 'resolved';

  // Save / delete / undo → recheck THIS finding only. The list itself never
  // rebuilds while the modal is open; the row just tells the user what its own
  // change achieved.
  async function handleEntryChanged(updatedResume: ResumeJSON) {
    if (!onRecheck) return;
    setRecheck('checking');
    try {
      setRecheck(await onRecheck(f, updatedResume));
    } catch {
      setRecheck('error');
    }
  }

  return (
    <li
      className="rounded border border-gray-200/80 bg-white/60"
      data-testid="scorecard-finding"
      data-severity={f.severity}
      data-resolved={resolved ? '1' : '0'}
      data-recheck={recheck || ''}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 text-xs text-left px-2 py-1.5 hover:bg-gray-50 rounded"
      >
        {resolved ? (
          <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" aria-label="Resolved" />
        ) : (
          <span
            className={`w-2 h-2 rounded-full mt-1 ml-0.5 mr-0.5 flex-shrink-0 ${complete ? 'bg-green-500' : SEV_DOT[f.severity]}`}
            data-testid={complete ? 'scorecard-complete-dot' : undefined}
          />
        )}
        <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
          <SectionChip section={f.section} />
          {complete && (
            <span
              className="inline-flex items-center mr-1.5 px-1.5 py-px rounded-full border border-green-200 bg-green-50 text-[10px] font-semibold uppercase tracking-wide text-green-700 align-[1px]"
              data-testid="scorecard-complete-pill"
            >
              Complete
            </span>
          )}
          <span
            className={`${resolved ? 'text-gray-400 line-through' : 'text-gray-700'}${/^Rule-of-three/.test(f.label) ? ' underline decoration-dotted decoration-gray-300 underline-offset-2 cursor-help' : ''}`}
            title={/^Rule-of-three/.test(f.label) ? RULE_OF_THREE_HELP : undefined}
          >{label}</span>
          <span className="text-gray-400"> · {locator}</span>
          {recheck === 'checking' && (
            <span className="ml-1.5 text-[10px] text-gray-400">Rechecking…</span>
          )}
          {recheck === 'flagged' && (
            <span
              className="inline-flex items-center ml-1.5 px-1.5 py-px rounded-full border border-amber-200 bg-amber-50 text-[10px] font-medium text-amber-700 align-[1px]"
              data-testid="scorecard-still-flagged"
              title="Your edit was saved, but this item still trips the same check. Try rewording it, or use “Mark resolved” if it’s intentional."
            >
              Still flagged
            </span>
          )}
          {recheck === 'error' && (
            <span className="ml-1.5 text-[10px] text-gray-500">Saved — couldn’t recheck just now</span>
          )}
          {complete && (
            <span className="block text-[10px] text-green-700">Saved — this check now passes.</span>
          )}
          {!open && f.detail && <span className="block text-gray-400 italic truncate">“{f.detail}”</span>}
        </span>
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2" data-tier={tier}>
          {editors ? (
            <div className="space-y-2">
              {multi && (
                <p className="text-[11px] text-gray-500" data-testid="scorecard-multi-hint">
                  This finding covers {entries.length} items — edit any of them, or delete the one you
                  don’t want to keep. Deletions apply when you save your resume.
                </p>
              )}
              {entries.map((p) => (
                <FindingEntry
                  key={p}
                  lintPath={p}
                  resume={resume}
                  deletions={deletions}
                  onSaveItem={onSaveItem}
                  onDeleteSkill={onDeleteSkill}
                  onDeleteBullet={onDeleteBullet}
                  onUndoDelete={onUndoDelete}
                  onChanged={handleEntryChanged}
                />
              ))}
            </div>
          ) : (
            <>
              {/* Context = the finding's own SNIPPET (the text the linter
                  actually matched), never the anchor item's title — the
                  breadcrumb below already names that, and quoting it twice was
                  what made these rows unreadable. */}
              {quote && (
                <p className="text-xs text-gray-500 italic break-words [overflow-wrap:anywhere]" data-testid="scorecard-reference-quote">
                  “{quote}”
                </p>
              )}
              {tier === 'reference'
                // Names WHERE the item lives, as plain text. It isn't editable
                // here, so it must not look like a form control.
                ? entries.map((p) => (
                  <p key={p} className="text-xs text-gray-500" data-testid="scorecard-entry-reference">
                    <span className="font-medium text-gray-700">{humanizePath(p, resume)}</span>
                    {' — edit this item in its resume section.'}
                  </p>
                ))
                // Structural: nothing to edit at all. One muted line explains the
                // absence; anything more would be the fake editor again.
                : (
                  <p className="text-[11px] text-gray-400" data-testid="scorecard-structural-note">
                    {scope
                      ? `Applies to your ${scope} section as a whole — there’s no single item to edit here.`
                      : 'Applies across your resume — there’s no single item to edit here.'}
                  </p>
                )}
            </>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                // Marking resolved also COLLAPSES the row (owner 2026-07-27):
                // the item is done, so it returns to its single-line resolved
                // state and the eye moves to the next one. Un-resolving leaves
                // expansion exactly as the user left it.
                setOpen((o) => rowOpenAfterResolveToggle(o, resolved));
                onToggleResolved();
              }}
              data-testid="scorecard-resolve-toggle"
              className={`px-2.5 py-1 text-xs font-medium rounded border ${
                resolved
                  ? 'text-gray-500 border-gray-300 hover:bg-gray-50'
                  : 'text-green-700 border-green-300 hover:bg-green-50'
              }`}
            >
              {resolved ? 'Mark unresolved' : 'Mark resolved'}
            </button>
            {dictWord && onAddToDictionary && (
              <button
                type="button"
                onClick={async () => {
                  setDictState('adding');
                  try {
                    await onAddToDictionary(dictWord);
                    // The word's findings are now gone from the list, so this
                    // row is on its way out; collapsing keeps the transition
                    // quiet and matches the Mark-resolved behaviour.
                    setOpen(false);
                    setDictState('idle');
                  } catch {
                    // Nothing was cleared — say so and leave the row alone.
                    setDictState('error');
                  }
                }}
                disabled={dictState === 'adding'}
                data-testid="scorecard-add-to-dictionary"
                data-word={dictWord}
                title={`Stop flagging “${dictWord}” as a misspelling — on this and every other resume`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-40"
              >
                <BookPlus className="w-3.5 h-3.5" />
                {dictState === 'adding' ? 'Adding…' : 'Add to My Dictionary'}
              </button>
            )}
            {dictState === 'error' && (
              <span className="text-[11px] text-red-600" data-testid="scorecard-dictionary-error">
                Couldn’t save to your dictionary — please try again.
              </span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

interface DimensionCardProps {
  /** FROZEN dimension — owns the findings rows and their count. */
  dim: DimensionScore;
  /**
   * The same dimension recomputed from the LIVE resume + the shared lint
   * findings (what the rail and chip score). Owns the NUMBER, bar, colour,
   * blurb and the "zeroed" badge, so a fix applied in this modal shows up here
   * immediately instead of waiting for a reopen. Absent → the frozen values.
   */
  live?: DimensionScore;
  resume: ResumeJSON;
  resolvedIds: Set<string>;
  deletions: ScorecardDeletion[];
  onToggleResolved: (id: string) => void;
  onSaveItem?: (path: FindingPath, newText: string) => Promise<void> | void;
  onDeleteSkill?: (index: number) => ResumeJSON;
  onDeleteBullet?: (at: BulletLocation) => ResumeJSON;
  onUndoDelete?: (logIndex: number) => ResumeJSON;
  onRecheck?: (finding: Finding, updatedResume: ResumeJSON) => Promise<RecheckResult>;
  onAddToDictionary?: (word: string) => Promise<void>;
  /** Start expanded even when this isn't Accuracy (high-severity focus, BR-1). */
  startOpen?: boolean;
}

function DimensionCard({
  dim, live, resume, resolvedIds, deletions, onToggleResolved, onSaveItem,
  onDeleteSkill, onDeleteBullet, onUndoDelete, onRecheck, onAddToDictionary, startOpen,
}: DimensionCardProps) {
  const [open, setOpen] = useState(startOpen || dim.key === 'correctness');
  // Number live, list frozen: the score must never disagree with the rail, and
  // the rows must never reshuffle or vanish under the user mid-read.
  const shown = live ?? dim;
  const c = scoreColor(shown.score);
  const hasFindings = dim.findings.length > 0;
  const unresolvedCount = dim.findings.filter((f) => !resolvedIds.has(f.id!)).length;
  return (
    <div className={`border rounded-lg ${c.bg} border-gray-200`} data-testid={`scorecard-dim-${dim.key}`} data-score={shown.score === null ? '' : String(shown.score)}>
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base font-semibold text-gray-800">{dim.label}</span>
            {dim.key === 'correctness' && <span className="text-[10px] uppercase tracking-wide text-gray-400">fix first</span>}
            {!!shown.zeroedBy && (
              <span className="text-[10px] uppercase tracking-wide text-red-600 font-semibold">zeroed</span>
            )}
          </div>
          <div className="flex flex-col items-end flex-shrink-0 leading-tight">
            <span className={`text-sm font-bold ${c.text}`} data-testid={`scorecard-dim-${dim.key}-score`}>
              {shown.score === null ? 'N/A' : `${shown.score}/100`}
            </span>
            <span className="text-[10px] text-gray-400">({Math.round(WEIGHTS[dim.key] * 100)}% of overall score)</span>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
          <div className={`h-full ${c.bar}`} style={{ width: `${shown.score ?? 0}%` }} />
        </div>
        <p className="text-xs text-gray-600 mt-2">{shown.blurb}</p>
      </div>
      {hasFindings && (
        <div className="border-t border-gray-200/70">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {dim.findings.length} {dim.findings.length === 1 ? 'item' : 'items'} to look at
            {unresolvedCount < dim.findings.length && (
              <span className="text-green-600">· {dim.findings.length - unresolvedCount} resolved</span>
            )}
          </button>
          {open && (
            <ul className="px-3 pb-3 space-y-1.5 max-h-80 overflow-y-auto">
              {/* No cap — show every finding; the list scrolls (max-h-80). */}
              {dim.findings.map((f) => (
                <FindingRow
                  key={f.id}
                  finding={f}
                  resume={resume}
                  resolved={resolvedIds.has(f.id!)}
                  deletions={deletions}
                  onToggleResolved={() => onToggleResolved(f.id!)}
                  onSaveItem={onSaveItem}
                  onDeleteSkill={onDeleteSkill}
                  onDeleteBullet={onDeleteBullet}
                  onUndoDelete={onUndoDelete}
                  onRecheck={onRecheck}
                  onAddToDictionary={onAddToDictionary}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ScorecardModal({
  isOpen, onClose, resume, resumename, onSaveItem, onResolvedChange, onLintFindings,
  initialLintFindings, onEditorMutate, onBatchFix, focusHigh,
}: ScorecardModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);
  // Rule-of-three tolerance is an admin-tweakable global setting (fraction of
  // resume items that may be triple-lists for free). Falls back to the default
  // if the setting is absent or the fetch fails.
  const [r3Tolerance, setR3Tolerance] = useState(DEFAULT_RULE_OF_THREE_TOLERANCE);
  const [quantTarget, setQuantTarget] = useState(DEFAULT_QUANTIFICATION_TARGET);
  useEffect(() => {
    api.getPublicSetting('rule_of_three_tolerance')
      .then((d) => {
        const v = Number(d?.setting_value);
        if (Number.isFinite(v) && v >= 0 && v < 1) setR3Tolerance(v);
      })
      .catch(() => { /* keep default */ });
    api.getPreferences()
      .then((p) => setLearned(p?.custom_dictionary || []))
      .catch(() => { /* no dictionary → nothing to filter */ });
    api.getPublicSetting('quantification_target_rate')
      .then((d) => {
        const v = Number(d?.setting_value);
        if (Number.isFinite(v) && v > 0 && v <= 1) setQuantTarget(v);
      })
      .catch(() => { /* keep default */ });
  }, []);
  // Correctness dimension: deterministic lint findings from the server
  // (spelling, doubled words, tense drift, duplicate skills…). The LIVE
  // in-memory resume is sent, so unsaved edits are included. Free for every
  // user — no LLM, no usage meter. `null` = still loading; on failure the
  // dimension shows an error blurb with score null and the overall score
  // simply excludes it.
  const [lintFindings, setLintFindings] = useState<LintFinding[] | null>(initialLintFindings ?? null);
  const [lintError, setLintError] = useState(false);
  // Confirmation shown after "Add to My Dictionary": which word was learned and
  // how many findings that settled. Session-only — reopening re-lints anyway.
  const [dictNotice, setDictNotice] = useState<{ word: string; cleared: number } | null>(null);
  /**
   * The user's personal wordlist. Every lint result this modal handles is
   * filtered through it (`effectiveLint`), so a word the user has taught is
   * gone from the list the moment it is stored — no waiting on a round trip,
   * and correct whether or not the server also filters.
   */
  const [learned, setLearned] = useState<string[]>([]);
  // Latest findings the editor already holds — read when this modal opens so a
  // reopen never blanks a number that is already known (only a genuinely
  // first-ever pass shows the "calculating" placeholder).
  const seedRef = useRef(initialLintFindings ?? null);
  seedRef.current = initialLintFindings ?? null;
  // Read the resume at fetch time without re-firing the request on every
  // keystroke: the scorecard lints what's on screen when it opens.
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  // The resumename only addresses the endpoint (the body carries the resume
  // that actually gets linted), so when the prop isn't supplied a sanitized
  // candidate name is a safe stand-in — path-unsafe characters are stripped so
  // API Gateway can't reject the request.
  const lintName = resumename || resume.name?.replace(/[^\w .-]/g, '').trim() || 'resume';
  // Latest hand-up callback, held in a ref so a parent re-render never re-fires
  // the lint request.
  const lintUpRef = useRef(onLintFindings);
  lintUpRef.current = onLintFindings;
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLintFindings(seedRef.current);
    setLintError(false);
    api.lintResume(lintName, resumeRef.current)
      .then((d) => {
        if (cancelled) return;
        setLintFindings(d?.findings || []);
        setLintError(false);
        lintUpRef.current?.(d?.findings || []);
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to whatever the editor's shared pass already had (same rule
        // as the shared reducer: a failed refresh keeps the last good findings);
        // only a cold failure drops to the three-dimension score.
        setLintFindings(seedRef.current);
        setLintError(seedRef.current === null);
      });
    return () => { cancelled = true; };
  }, [isOpen, lintName]);

  /**
   * The lint findings AFTER the user's dictionary is applied — the single list
   * every score, count and row in this modal is built from.
   */
  const effectiveLint = useMemo(
    () => (lintFindings === null ? null : applyDictionary(lintFindings, learned)),
    [lintFindings, learned]);

  // Manually-resolved finding IDs. Seeded from the resume (persisted via
  // onResolvedChange → saved with the resume) so a dismissal survives across
  // sessions, not just while the editor is open. RE-seeded on every open: this
  // modal is mounted (closed) alongside the editor, so the initializer runs
  // against the EMPTY pre-load resume — without the re-seed the modal scored
  // every persisted dismissal as still-open and disagreed with the rail/chip.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() => new Set(resume.scorecard_resolved || []));
  // The findings list is built from the resume AS IT WAS WHEN THE MODAL OPENED
  // and stays put for the session. Fixing one item must never reshuffle,
  // renumber or silently delete rows out from under the user mid-read — the
  // edited row reports its own outcome in place (green dot + "Complete", or
  // "Still flagged"), and closing/reopening re-runs every check from scratch.
  const [frozenResume, setFrozenResume] = useState<ResumeJSON>(resume);
  /**
   * Deletions (skills AND experience bullets) applied in THIS modal session, in
   * the order they happened. The frozen findings above still carry the
   * `skills[i]` / `…bullets[b]` paths that were true when the modal opened, so
   * every one of them is re-addressed through this log (`resolveLintPath`)
   * before it is read, saved or deleted — otherwise the renumbering caused by a
   * splice would point a pending edit at the wrong item. Cleared on open: a
   * reopened modal freezes a fresh, already-shifted resume, so replaying old
   * deletions would double-count them.
   */
  const [deletions, setDeletions] = useState<ScorecardDeletion[]>([]);
  // Mirror of the log for the handlers: two clicks in one tick must both build
  // on the newest log, not on the render-time snapshot.
  const deletionsRef = useRef<ScorecardDeletion[]>(deletions);
  const setLog = useCallback((next: ScorecardDeletion[]) => {
    deletionsRef.current = next;
    setDeletions(next);
  }, []);
  // ── "Fix all N safe issues" (BR-2) ────────────────────────────────────────
  // Additive: the editor's Apply All / Dismiss All recommendation controls are
  // untouched. This applies the LINTER's mechanically-safe rewrites only.
  const [fixOpen, setFixOpen] = useState(false);
  const [fixUnchecked, setFixUnchecked] = useState<Set<string>>(new Set());
  const [fixBusy, setFixBusy] = useState(false);
  const [fixError, setFixError] = useState(false);
  /** Pre-batch resume, kept for the single Undo. Cleared once undone. */
  const [fixSnapshot, setFixSnapshot] = useState<ResumeJSON | null>(null);
  const [fixApplied, setFixApplied] = useState(0);
  // Show only high-severity rows — how BR-1's master-resume warning arrives.
  const [onlyHigh, setOnlyHigh] = useState(!!focusHigh);
  useEffect(() => {
    if (!isOpen) return;
    setFrozenResume(resumeRef.current);
    setResolvedIds(new Set(resumeRef.current.scorecard_resolved || []));
    setLog([]);
    setDictNotice(null);
    setFixOpen(false);
    setFixUnchecked(new Set());
    setFixBusy(false);
    setFixError(false);
    setFixSnapshot(null);
    setFixApplied(0);
    setOnlyHigh(!!focusHigh);
  }, [isOpen, setLog, focusHigh]);

  /**
   * Apply an in-memory resume change for the merge flow. The editor owns the
   * state (functional update, so concurrent edits compose), and `resumeRef` is
   * advanced here too so two changes in the same tick still build on each other
   * rather than on a stale prop.
   */
  const mutateResume = useCallback((fn: (r: ResumeJSON) => ResumeJSON): ResumeJSON => {
    const next = fn(resumeRef.current);
    resumeRef.current = next;
    onEditorMutate?.(fn);
    return next;
  }, [onEditorMutate]);

  /** Delete `skills[index]` (LIVE index) and record it so paths can be remapped. */
  const deleteSkill = useCallback((index: number): ResumeJSON => {
    const item = (resumeRef.current.skills || [])[index];
    if (!item) return resumeRef.current;
    const next = mutateResume((r) => deleteSkillAt(r, index));
    setLog([...deletionsRef.current, { index, item }]);
    return next;
  }, [mutateResume, setLog]);

  /**
   * Delete one experience bullet (LIVE indices) and record it so paths can be
   * remapped. Emptying a role's bullet list is allowed — the editor's own bullet
   * remove does the same and the save-side validator only requires a list.
   */
  const deleteBullet = useCallback((at: BulletLocation): ResumeJSON => {
    const item = getBulletAt(resumeRef.current, at);
    if (!item) return resumeRef.current;
    const next = mutateResume((r) => deleteBulletAt(r, at));
    setLog([...deletionsRef.current, { kind: 'bullet', ...at, item }]);
    return next;
  }, [mutateResume, setLog]);

  /** Undo one recorded deletion, restoring that item at its original position. */
  const undoDelete = useCallback((logIndex: number): ResumeJSON => {
    const target = deletionsRef.current[logIndex];
    if (!target) return resumeRef.current;
    const next = mutateResume((r) => (isBulletDeletion(target)
      ? insertBulletAt(r, target, target.item)
      : insertSkillAt(r, target.index, target.item)));
    setLog(logAfterUndo(deletionsRef.current, logIndex));
    return next;
  }, [mutateResume, setLog]);

  /**
   * Every mechanically-safe rewrite available right now, computed from the LIVE
   * resume (so a row the user already fixed by hand drops out) and net of the
   * findings they marked resolved — dismissing means "leave this alone".
   */
  const safeFixes = useMemo(() => {
    const skip = resolvedSafeFixKeys(resume, effectiveLint, resolvedIds);
    return collectSafeFixes(resume, effectiveLint, skip);
  }, [resume, effectiveLint, resolvedIds]);
  const chosenFixes = useMemo(
    () => safeFixes.filter((f) => !fixUnchecked.has(f.key)),
    [safeFixes, fixUnchecked]);

  /** Re-lint after a batch so the ring, rail and chip describe the new text. */
  async function refreshLintAfterBatch(updated: ResumeJSON) {
    try {
      const d = await api.lintResume(lintName, updated, learned);
      const fresh = d?.findings || [];
      setLintFindings(fresh);
      lintUpRef.current?.(applyDictionary(fresh, learned));
    } catch {
      /* the fixes are saved; a stale score corrects itself on the next pass */
    }
  }

  /**
   * Apply the checked fixes: ONE in-memory mutation, ONE save (per BR-2 — N
   * saves would stampede tag generation). The pre-batch resume is snapshotted
   * first so a single Undo can put everything back.
   */
  async function handleApplyFixes() {
    if (!onBatchFix || chosenFixes.length === 0 || fixBusy) return;
    const batch: SafeFix[] = chosenFixes;
    const snapshot = resumeRef.current;
    setFixBusy(true);
    setFixError(false);
    try {
      await onBatchFix((r) => applySafeFixes(r, batch));
      const updated = applySafeFixes(snapshot, batch);
      resumeRef.current = updated;
      setFixSnapshot(snapshot);
      setFixApplied(batch.length);
      setFixOpen(false);
      setFixUnchecked(new Set());
      void refreshLintAfterBatch(updated);
    } catch {
      // Nothing is claimed unless the save succeeded.
      setFixError(true);
    } finally {
      setFixBusy(false);
    }
  }

  /** Single Undo — restore the exact pre-batch resume, saved once. */
  async function handleUndoFixes() {
    if (!onBatchFix || !fixSnapshot || fixBusy) return;
    const snapshot = fixSnapshot;
    setFixBusy(true);
    setFixError(false);
    try {
      await onBatchFix(() => snapshot);
      resumeRef.current = snapshot;
      setFixSnapshot(null);
      setFixApplied(0);
      void refreshLintAfterBatch(snapshot);
    } catch {
      setFixError(true);
    } finally {
      setFixBusy(false);
    }
  }
  // Scores are computed NET of resolved findings: a resolved finding leaves the
  // penalty math, the counts, the blurbs AND the high-severity zero-out rule, so
  // dismissing one immediately recovers the dimension (and the overall).
  const card = useMemo(
    () => buildScorecard(frozenResume, {
      ruleOfThreeTolerance: r3Tolerance,
      quantificationTarget: quantTarget,
      lintFindings: effectiveLint,
      lintError,
      resolvedIds,
    }),
    [frozenResume, r3Tolerance, quantTarget, effectiveLint, lintError, resolvedIds]);

  /**
   * The SCORES, recomputed from the live state the rail and chip use: the
   * editor's current resume plus the shared lint findings (`initialLintFindings`
   * is that shared state, re-sent on every editor render — including the fresh
   * pass a row recheck hands up). So a fix made in this modal moves the ring and
   * the dimension numbers immediately, and ring == rail == chip at all times.
   *
   * ONLY the numbers come from here. The findings LISTS (and their counts) stay
   * on the frozen card above: fixing one item must never reshuffle, renumber or
   * silently delete rows out from under the reader — each edited row reports its
   * own outcome in place with a Complete pill, which is also what explains a
   * green dimension that still lists rows.
   */
  const liveCard = useMemo(
    () => buildScorecard(resume, {
      ruleOfThreeTolerance: r3Tolerance,
      quantificationTarget: quantTarget,
      lintFindings: applyDictionary(initialLintFindings ?? lintFindings ?? [], learned),
      lintError,
      resolvedIds,
    }),
    [resume, r3Tolerance, quantTarget, initialLintFindings, lintFindings, learned, lintError, resolvedIds]);
  const liveDim = (key: DimensionScore['key']) => liveCard.dimensions.find((d) => d.key === key);

  /**
   * Recheck ONE finding right after its row changed — an inline SAVE, a merge
   * DELETE or an UNDO. The caller hands in the resume AS IT NOW STANDS (derived,
   * not read back from props) so the answer never depends on the parent's state
   * having flushed yet. Local dimensions are recomputed in the browser; a
   * correctness finding costs one free lint call, whose fresh findings are also
   * handed to the editor so the rail number reflects the merge without a reopen.
   */
  async function runRecheck(finding: Finding, updated: ResumeJSON): Promise<RecheckResult> {
    const dim = findingDimension(finding);
    const before = card.dimensions.find((d) => d.key === dim)?.findings ?? [];
    return recheckFinding(finding, before, updated, {
      ruleOfThreeTolerance: r3Tolerance,
      quantificationTarget: quantTarget,
      lintFindings: effectiveLint,
      relint: (r) => api.lintResume(lintName, r, learned).then((d) => {
        const fresh = applyDictionary(d?.findings || [], learned);
        // Share the fresh pass with the editor so the rail score and this
        // modal's Complete pills never describe different resumes. The modal's
        // OWN list stays frozen on purpose — reopening rebuilds it.
        lintUpRef.current?.(fresh);
        return fresh;
      }),
    });
  }
  const toggleResolved = (id: string) => setResolvedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    onResolvedChange?.(Array.from(next));
    return next;
  });

  /**
   * "Add to My Dictionary" — teach the spell-checker one word, for good.
   *
   * ORDER MATTERS. The preferences write happens FIRST and is allowed to
   * throw: nothing is cleared from the UI unless the word is actually stored,
   * so a failed write leaves every finding exactly where it was (the row shows
   * its own error hint). Only then do we drop the word's findings — from this
   * modal's list AND from the editor's shared state, so ring, rail and chip
   * agree in the same tick.
   *
   * A confirming re-lint follows, carrying the freshly-written list in the
   * request body so it can't race the preferences write server-side. If that
   * re-lint fails we keep the optimistic result rather than resurrecting rows
   * the user has already settled.
   */
  async function addWordToDictionary(word: string): Promise<void> {
    // 1. Persist FIRST, and let it throw. Nothing is cleared unless the word is
    //    actually stored, so a failed write leaves the list exactly as it was.
    const prefs = await api.getPreferences();
    const next = addToDictionary(prefs?.custom_dictionary, word);
    if (next) await api.updatePreferences({ custom_dictionary: next });
    const dictionary = next || prefs?.custom_dictionary || [];

    // 2. Teach the client. `effectiveLint` re-derives immediately, so EVERY row
    //    for this word disappears at once — not just the one that was clicked.
    const before = effectiveLint || [];
    const cleared = before.length - applyDictionary(before, dictionary).length;
    setLearned(dictionary);
    setDictNotice({ word, cleared });
    const shared = initialLintFindings ?? lintFindings;
    if (shared) lintUpRef.current?.(applyDictionary(shared, dictionary));

    // 3. Confirm with the server, carrying the freshly-written list in the body
    //    so the re-lint can't race the preferences write. A failure here is
    //    harmless: the word is saved and the client already filters it.
    try {
      const d = await api.lintResume(lintName, resumeRef.current, dictionary);
      const fresh = d?.findings || [];
      setLintFindings(fresh);
      lintUpRef.current?.(applyDictionary(fresh, dictionary));
    } catch {
      /* keep the local result */
    }
  }

  if (!isOpen) return null;
  // Accuracy is 45% of the overall, so until its pass lands there IS no overall
  // to report — the ring shows the placeholder instead of a soon-to-drop number.
  // A FAILED pass is different: the three-dimension score is then the answer.
  const lintPending = lintFindings === null && !lintError;
  const oc = scoreColor(liveCard.overall);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Resume Scorecard">
      {/* max-w one step wider than the old 860px so a full-width PDF bullet
          (743px + card chrome) is comfortable to edit; still viewport-capped by
          w-full, and the no-overflow guarantee comes from the editor sizing
          itself, not from this width. */}
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[960px] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Resume Scorecard</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex-1 min-h-0 overflow-y-auto">
          <div className={`flex items-center gap-4 p-4 rounded-lg ${lintPending ? 'bg-gray-50' : oc.bg} mb-4`}>
            <ScoreRing score={liveCard.overall} pending={lintPending} />
            <div>
              <div className="text-sm font-semibold text-gray-800">
                Overall readiness
                {lintPending && (
                  <span className="ml-2 text-[11px] font-medium text-gray-500 animate-pulse" data-testid="scorecard-overall-calculating">
                    Calculating resume score…
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                A deterministic read of how your resume comes across. <span className="font-medium">Accuracy</span> carries the
                most weight — a typo or grammar slip is high severity and zeroes its dimension until you fix it (or mark it
                resolved). <span className="font-medium">Authenticity</span> estimates how human (vs. AI-generated) the writing
                sounds — higher is better.
              </p>
            </div>
          </div>

          {dictNotice && (
            <div
              className="flex items-start gap-2 mb-3 px-3 py-2 rounded-lg border border-green-200 bg-green-50 text-xs text-green-800"
              data-testid="scorecard-dictionary-notice"
              data-word={dictNotice.word}
              data-cleared={String(dictNotice.cleared)}
            >
              <BookPlus className="w-4 h-4 flex-shrink-0 mt-px" />
              <span>
                Added <span className="font-semibold">“{dictNotice.word}”</span> to your dictionary
                {dictNotice.cleared > 0 && (
                  <> — {dictNotice.cleared} spelling {dictNotice.cleared === 1 ? 'note' : 'notes'} cleared</>
                )}
                . It won’t be flagged again on any of your resumes.
              </span>
            </div>
          )}

          {/* ── Fix all safe issues (BR-2) ─────────────────────────────────
              Additive next to the editor's Apply All / Dismiss All, which are
              untouched. Only the linter's mechanically-safe rewrites appear
              here; anything requiring judgment stays a row to read. */}
          {onBatchFix && (safeFixes.length > 0 || fixSnapshot) && (
            <div
              className="mb-3 rounded-lg border border-blue-200 bg-blue-50"
              data-testid="scorecard-fixall"
              data-available={String(safeFixes.length)}
            >
              <div className="flex items-center justify-between gap-3 p-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Wand2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-900">
                    {safeFixes.length > 0
                      ? <>
                        <span className="font-semibold">
                          {safeFixes.length} safe {safeFixes.length === 1 ? 'fix' : 'fixes'}
                        </span>
                        {' '}— spelling, doubled words and tense slips we can correct exactly.
                      </>
                      : <>All safe fixes are applied.</>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {fixSnapshot && (
                    <button
                      type="button"
                      onClick={handleUndoFixes}
                      disabled={fixBusy}
                      data-testid="scorecard-fixall-undo"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-white disabled:opacity-40"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Undo {fixApplied}
                    </button>
                  )}
                  {safeFixes.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFixOpen((o) => !o)}
                      data-testid="scorecard-fixall-toggle"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                    >
                      {fixOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      Fix all {safeFixes.length} safe {safeFixes.length === 1 ? 'issue' : 'issues'}
                    </button>
                  )}
                </div>
              </div>

              {fixSnapshot && !fixBusy && (
                <p className="px-3 pb-2 -mt-1 text-[11px] text-blue-800" data-testid="scorecard-fixall-applied">
                  {fixApplied} {fixApplied === 1 ? 'fix' : 'fixes'} applied and saved. Undo puts your
                  resume back exactly as it was.
                </p>
              )}
              {fixError && (
                <p className="px-3 pb-2 text-[11px] text-red-700" data-testid="scorecard-fixall-error">
                  Couldn’t save — nothing was changed. Please try again.
                </p>
              )}

              {fixOpen && safeFixes.length > 0 && (
                <div className="border-t border-blue-200 p-3 space-y-2">
                  <p className="text-[11px] text-blue-800">
                    Review before applying — everything is checked by default. Applying makes one
                    change to your resume and saves it once.
                  </p>
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                    {safeFixes.map((f) => {
                      const checked = !fixUnchecked.has(f.key);
                      return (
                        <li
                          key={f.key}
                          className="rounded border border-blue-200 bg-white p-2"
                          data-testid="scorecard-fix-row"
                          data-rule={f.rule}
                          data-checked={checked ? '1' : '0'}
                        >
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setFixUnchecked((prev) => {
                                const next = new Set(prev);
                                if (next.has(f.key)) next.delete(f.key); else next.add(f.key);
                                return next;
                              })}
                              className="mt-0.5 flex-shrink-0"
                              aria-label={`Apply fix: ${f.label}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block text-[11px] text-gray-500">
                                {f.section} · {f.label}
                              </span>
                              <span className="block text-xs text-red-700 line-through break-words [overflow-wrap:anywhere]">
                                {f.before}
                              </span>
                              <span className="block text-xs text-green-800 break-words [overflow-wrap:anywhere]">
                                {f.after}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleApplyFixes}
                      disabled={fixBusy || chosenFixes.length === 0}
                      data-testid="scorecard-fixall-apply"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
                    >
                      {fixBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {fixBusy
                        ? 'Applying…'
                        : `Apply ${chosenFixes.length} ${chosenFixes.length === 1 ? 'fix' : 'fixes'}`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFixOpen(false)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* High-severity focus (BR-1 entry point) */}
          {onlyHigh && (
            <div
              className="mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900"
              data-testid="scorecard-highfilter"
            >
              <span>
                Showing the high-severity items only — these are the ones copied onto every
                resume you generate.
              </span>
              <button
                type="button"
                onClick={() => setOnlyHigh(false)}
                data-testid="scorecard-highfilter-clear"
                className="flex-shrink-0 px-2 py-1 font-medium border border-amber-300 rounded hover:bg-amber-100"
              >
                Show everything
              </button>
            </div>
          )}

          <div className="space-y-3">
            {card.dimensions.map((d) => (
              <DimensionCard
                key={d.key}
                dim={onlyHigh ? { ...d, findings: d.findings.filter((f) => f.severity === 'high') } : d}
                live={liveDim(d.key)}
                resume={resume}
                resolvedIds={resolvedIds}
                deletions={deletions}
                onToggleResolved={toggleResolved}
                onSaveItem={onSaveItem}
                onDeleteSkill={onEditorMutate ? deleteSkill : undefined}
                onDeleteBullet={onEditorMutate ? deleteBullet : undefined}
                onUndoDelete={onEditorMutate ? undoDelete : undefined}
                onRecheck={onSaveItem ? runRecheck : undefined}
                onAddToDictionary={addWordToDictionary}
                startOpen={onlyHigh}
              />
            ))}
          </div>

          <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
            This is a guide, not a gate — it flags patterns, it doesn’t rewrite. Numbers and ambiguous words are scored
            conservatively, so use your judgment. For AI-assisted fixes, run <span className="italic">Review Resume and Generate Recommendations</span>.
          </p>
        </div>

        {/* Severity legend — PINNED to the bottom of the lightbox so it stays
            readable while the findings list scrolls. A flex sibling of the
            scroll pane (not an overlay), so it can never cover the last row.
            Uses the same dot colors as FindingRow so it stays in sync. */}
        <div
          className="flex-shrink-0 flex items-center justify-between flex-wrap gap-x-3 gap-y-1 px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg text-[11px] text-gray-600"
          data-testid="scorecard-legend"
        >
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${SEV_DOT.high}`} /> High — fix first
          </span>
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${SEV_DOT.med}`} /> Medium — worth revising
          </span>
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${SEV_DOT.low}`} /> Low — minor / optional
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Complete — fixed just now
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-600" /> Resolved
          </span>
        </div>
      </div>
    </div>
  );
}
